import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, snapCenterToCursor } from '@dnd-kit/modifiers';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { BookOpen, Search, X, Star, Plus, GripVertical, Trash2, MoreVertical, ArrowUp, ArrowDown, Edit2, Loader2, Check, Trophy, ChevronRight, Users, Link, Copy, Sparkles, Info, List as ListIcon, Eye, Calendar as CalendarIcon, ThumbsUp, Meh, ThumbsDown, ArrowRight, ListOrdered, Tag, Rocket, Wand2, Heart, Skull, Ghost, Clock, Map, Baby, Lightbulb, Briefcase, FlaskConical, Feather, Mountain, Drama, BookText, GraduationCap, Brain, Palette, Building2, Swords, Plane, Utensils } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';
import { format } from 'date-fns';
import { getBookDetails } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Genre configuration for books with icons
const genreConfig = {
  'Fiction': { icon: BookText, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  'Non-Fiction': { icon: GraduationCap, color: 'bg-slate-50 text-slate-600 border-slate-200' },
  'Mystery': { icon: Search, color: 'bg-purple-50 text-purple-600 border-purple-200' },
  'Science Fiction': { icon: Rocket, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  'Fantasy': { icon: Wand2, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  'Romance': { icon: Heart, color: 'bg-pink-50 text-pink-600 border-pink-200' },
  'Thriller': { icon: Skull, color: 'bg-red-50 text-red-600 border-red-200' },
  'Horror': { icon: Ghost, color: 'bg-neutral-50 text-neutral-700 border-neutral-300' },
  'Biography': { icon: Users, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  'Memoir': { icon: Feather, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  'History': { icon: Clock, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  'Historical Fiction': { icon: Clock, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  'Science': { icon: FlaskConical, color: 'bg-teal-50 text-teal-600 border-teal-200' },
  'Self-Help': { icon: Lightbulb, color: 'bg-green-50 text-green-600 border-green-200' },
  'Business': { icon: Briefcase, color: 'bg-blue-50 text-blue-600 border-blue-200' },
  'Children\'s': { icon: Baby, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  'Young Adult': { icon: Sparkles, color: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200' },
  'Classics': { icon: BookOpen, color: 'bg-stone-50 text-stone-600 border-stone-200' },
  'Adventure': { icon: Map, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  'Crime': { icon: Search, color: 'bg-slate-50 text-slate-700 border-slate-300' },
  'Dystopian': { icon: Building2, color: 'bg-gray-50 text-gray-700 border-gray-300' },
  'Contemporary': { icon: Drama, color: 'bg-rose-50 text-rose-600 border-rose-200' },
  'Literary Fiction': { icon: Feather, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'Philosophy': { icon: Brain, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'Psychology': { icon: Brain, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  'Poetry': { icon: Feather, color: 'bg-pink-50 text-pink-500 border-pink-200' },
  'Graphic Novel': { icon: Palette, color: 'bg-sky-50 text-sky-600 border-sky-200' },
  'Comics': { icon: Palette, color: 'bg-sky-50 text-sky-600 border-sky-200' },
  'Cookbooks': { icon: Utensils, color: 'bg-orange-50 text-orange-500 border-orange-200' },
  'Travel': { icon: Plane, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  'Action': { icon: Swords, color: 'bg-red-50 text-red-600 border-red-200' },
  'Western': { icon: Mountain, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  'Middle Grade': { icon: Baby, color: 'bg-lime-50 text-lime-600 border-lime-200' },
};

const getGenreConfig = (genre) => {
  return genreConfig[genre] || { icon: BookOpen, color: 'bg-slate-50 text-slate-600 border-slate-200' };
};

// Combined Book View Component for Joint Lists
function CombinedBookView({ combinedView }) {
  const { common_books, unique_picks } = combinedView;
  
  return (
    <div className="space-y-6">
      {/* Common Books Section */}
      {common_books?.length > 0 && (
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg text-[#3c405b] flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Common Favorites ({common_books.length})
            </CardTitle>
            <p className="text-xs text-[#64748B]">Books everyone has ranked, combined by popularity</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {common_books.map((book, idx) => (
                <div key={book.book_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <span className="font-outfit font-bold text-xl text-[#3c405b] w-8 text-center">
                    {idx + 1}
                  </span>
                  <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100">
                    {book.cover_image ? (
                      <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#3c405b] truncate">{book.title}</h3>
                    <p className="text-sm text-[#64748B] truncate">{book.author}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Unique Picks Section */}
      {unique_picks?.length > 0 && (
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg text-[#3c405b] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Unique Picks ({unique_picks.length})
            </CardTitle>
            <p className="text-xs text-[#64748B]">Books only ranked by one member - discover new reads!</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {unique_picks.map((book) => (
                <div key={book.book_id} className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                    {book.from_user_picture ? (
                      <img src={book.from_user_picture} alt={book.from_user} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs font-medium">
                        {book.from_user?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100">
                    {book.cover_image ? (
                      <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#3c405b] truncate">{book.title}</h3>
                    <p className="text-sm text-[#64748B] truncate">{book.author}</p>
                    <p className="text-xs text-purple-600">
                      Added by {book.from_user}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Empty state */}
      {(!common_books || common_books.length === 0) && (!unique_picks || unique_picks.length === 0) && (
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardContent className="p-4 text-center py-12 text-slate-500">
            <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">No rankings yet</p>
            <p className="text-sm">Add books and rank them to see the combined view!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Sortable Rank Book Item for Ranking Dialog
function SortableRankBookItem({ book, rank, isNew }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: book.book_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
        isNew 
          ? 'bg-amber-50 border-amber-500 ring-2 ring-amber-200' 
          : 'bg-white border-slate-100'
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
        <GripVertical className="w-5 h-5 text-slate-400" />
      </div>
      <span className="font-outfit font-bold text-lg text-[#3c405b] w-8 text-center">
        {rank}
      </span>
      <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100">
        {book.cover_image ? (
          <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className={`font-medium truncate ${isNew ? 'text-amber-700' : 'text-[#3c405b]'}`}>
          {book.title}
        </h4>
        <p className="text-sm text-[#64748B] truncate">{book.author}</p>
      </div>
      {isNew && (
        <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
          New
        </span>
      )}
    </div>
  );
}

// Sortable Book Item Component
function SortableBookItem({ book, onRemove, onViewDetails, onMarkRead, isRead, isDragging }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: book.book_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  // Get primary genre info for icon
  const primaryGenre = book.genres?.[0];
  const gConfig = primaryGenre ? getGenreConfig(primaryGenre) : null;
  const GenreIcon = gConfig?.icon || BookOpen;
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white rounded-xl shadow-sm border border-slate-100 ${isDragging ? 'shadow-lg ring-2 ring-amber-500' : ''}`}
      data-testid={`book-item-${book.book_id}`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0">
        <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
      </div>
      
      <span className="font-outfit font-bold text-base sm:text-lg text-[#3c405b] w-6 sm:w-8 text-center flex-shrink-0">
        {book.rank}
      </span>
      
      {/* Clickable poster and title area */}
      <div 
        className="w-8 h-12 sm:w-10 sm:h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
        onClick={() => onViewDetails && onViewDetails(book)}
      >
        {book.cover_image ? (
          <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" />
          </div>
        )}
      </div>
      
      <div 
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => onViewDetails && onViewDetails(book)}
      >
        <div className="flex items-center gap-1.5">
          <h3 className="font-medium text-[#3c405b] truncate hover:text-amber-600 transition-colors text-sm sm:text-base flex-1">{book.title}</h3>
          {/* Genre Icon */}
          {primaryGenre && (
            <div className={`p-1 rounded hidden sm:flex ${gConfig?.color || 'bg-slate-100 text-slate-600'}`} title={primaryGenre}>
              <GenreIcon className="w-3 h-3" />
            </div>
          )}
        </div>
        <p className="text-xs sm:text-sm text-[#64748B] truncate">{book.author}</p>
        {book.series && (
          <p className="text-xs text-amber-600 truncate mt-0.5">
            {book.series.series_name} #{book.series.position || '?'}
          </p>
        )}
      </div>
      
      {/* Read Button */}
      {!isRead && onMarkRead && (
        <Button
          data-testid={`mark-read-btn-${book.book_id}`}
          onClick={() => onMarkRead(book)}
          size="icon"
          className="h-7 w-7 sm:h-8 sm:w-auto sm:px-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full flex-shrink-0"
        >
          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline ml-1 text-sm">Read</span>
        </Button>
      )}
      
      {isRead && (
        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full flex-shrink-0">
          <Check className="w-3 h-3" />
          <span className="hidden sm:inline">Read</span>
        </span>
      )}
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0">
            <MoreVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onViewDetails && onViewDetails(book)}>
            <Info className="w-4 h-4 mr-2" />View Details
          </DropdownMenuItem>
          {!isRead && onMarkRead && (
            <DropdownMenuItem onClick={() => onMarkRead(book)}>
              <Eye className="w-4 h-4 mr-2 text-amber-500" />Mark as Read
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onRemove(book)} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function BookListDetail({ user }) {
  const { id: listId } = useParams();
  const navigate = useNavigate();
  
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Rename state
  const [renameOpen, setRenameOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  
  // Ranking state for new additions (drag-and-drop mode)
  const [addingBook, setAddingBook] = useState(null);
  const [showRanking, setShowRanking] = useState(false);
  const [rankingBooks, setRankingBooks] = useState([]);
  
  // Search result details state
  const [searchResultDetails, setSearchResultDetails] = useState(null);
  const [loadingSearchDetails, setLoadingSearchDetails] = useState(false);
  
  // Book details dialog state
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookDetails, setBookDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Mark as Read dialog state
  const [markReadBook, setMarkReadBook] = useState(null);
  const [readBooks, setReadBooks] = useState([]);
  const [readGrade, setReadGrade] = useState('liked');
  const [readDate, setReadDate] = useState(new Date());
  const [readComment, setReadComment] = useState('');
  const [isFirstRead, setIsFirstRead] = useState(true);
  const [readPrimaryGenre, setReadPrimaryGenre] = useState(null);
  const [readCustomGenre, setReadCustomGenre] = useState('');
  const [markingRead, setMarkingRead] = useState(false);
  
  // Ranking comparison state for Mark as Read
  const [readComparisonStep, setReadComparisonStep] = useState(false);
  const [readExistingItems, setReadExistingItems] = useState([]);
  const [readComparisonItem, setReadComparisonItem] = useState(null);
  const [readSearchLow, setReadSearchLow] = useState(0);
  const [readSearchHigh, setReadSearchHigh] = useState(0);
  
  // Drag state
  const [activeId, setActiveId] = useState(null);
  
  // Joint list state
  const [rankingView, setRankingView] = useState('combined');
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isJointList = list?.list_type === 'joint';
  const memberCount = 1 + (list?.shared_with?.length || 0);
  const isOwner = list?.user_id === user?.user_id;

  // Get all members for ranking view toggle
  const getAllMembers = () => {
    if (!list) return [];
    const members = [{
      user_id: list.user_id,
      name: list.owner_name || 'Owner',
      picture: list.owner_picture
    }];
    if (list.shared_users) {
      members.push(...list.shared_users.map(u => ({
        user_id: u.user_id,
        name: u.name,
        picture: u.picture
      })));
    }
    return members;
  };

  // Get books for current view (combined or individual member)
  const getDisplayBooks = () => {
    if (!isJointList || rankingView === 'combined') {
      return list?.books || [];
    }
    // Individual member view
    const memberRanking = list?.user_rankings?.find(ur => ur.user_id === rankingView);
    if (!memberRanking) return [];
    
    const bookMap = {};
    (list?.books || []).forEach(b => { bookMap[b.book_id] = b; });
    
    return memberRanking.book_order
      .filter(bid => bookMap[bid])
      .map((bid, idx) => ({ ...bookMap[bid], rank: idx + 1 }));
  };

  // Get current user's personal books for joint list
  const getMyBooks = () => {
    if (!isJointList) return list?.books || [];
    const myRanking = list?.user_rankings?.find(ur => ur.user_id === user?.user_id);
    if (!myRanking) return [];
    
    const bookMap = {};
    (list?.books || []).forEach(b => { bookMap[b.book_id] = b; });
    
    return myRanking.book_order
      .filter(bid => bookMap[bid])
      .map((bid, idx) => ({ ...bookMap[bid], rank: idx + 1 }));
  };

  useEffect(() => {
    fetchList();
    fetchReadBooks();
  }, [listId]);

  const fetchList = async () => {
    try {
      const res = await axios.get(`${API}/book-lists/${listId}`, { withCredentials: true });
      setList(res.data);
    } catch (error) {
      toast.error('Failed to load list');
      navigate('/books');
    } finally {
      setLoading(false);
    }
  };

  const fetchReadBooks = async () => {
    try {
      const res = await axios.get(`${API}/read-books`, { withCredentials: true });
      setReadBooks(res.data || []);
    } catch (error) {
      console.error('Failed to fetch read books');
    }
  };

  const isBookRead = (bookId) => readBooks.some(b => b.book_id === bookId);

  const openMarkRead = (book) => {
    setMarkReadBook(book);
    setReadGrade('liked');
    setReadDate(new Date());
    setReadComment('');
    setIsFirstRead(true);
    setReadPrimaryGenre(book?.genres?.[0] || null);
    setReadComparisonStep(false);
    setReadExistingItems([]);
    setReadComparisonItem(null);
  };

  const handleProceedToRanking = async () => {
    // Get all read books with the same grade, sorted by rank
    const sameGradeBooks = readBooks
      .filter(b => b.grade === readGrade)
      .sort((a, b) => a.rank_in_group - b.rank_in_group);
    
    if (sameGradeBooks.length === 0) {
      // No other books with this grade, save directly at rank 1
      await saveReadBook(1);
    } else {
      // Start binary search comparison
      setReadExistingItems(sameGradeBooks);
      setReadSearchLow(0);
      setReadSearchHigh(sameGradeBooks.length);
      setReadComparisonStep(true);
      
      // Start with middle item
      const midIndex = Math.floor(sameGradeBooks.length / 2);
      setReadComparisonItem(sameGradeBooks[midIndex]);
    }
  };

  const handleReadComparisonChoice = async (preferNew) => {
    let low = readSearchLow;
    let high = readSearchHigh;
    
    if (preferNew) {
      // New book is better, search upper half (lower ranks = better)
      high = readExistingItems.findIndex(item => item.id === readComparisonItem.id);
    } else {
      // Comparison item is better, search lower half
      low = readExistingItems.findIndex(item => item.id === readComparisonItem.id) + 1;
    }
    
    if (low >= high) {
      // Found the position
      const rank = low + 1; // Rank is 1-indexed
      await saveReadBook(rank);
    } else {
      // Continue binary search
      setReadSearchLow(low);
      setReadSearchHigh(high);
      const midIndex = Math.floor((low + high) / 2);
      setReadComparisonItem(readExistingItems[midIndex]);
    }
  };

  const saveReadBook = async (rankInGroup) => {
    if (!markReadBook) return;
    
    setMarkingRead(true);
    try {
      await axios.post(`${API}/read-books/direct`, {
        book_id: markReadBook.book_id,
        title: markReadBook.title,
        author: markReadBook.author,
        cover_image: markReadBook.cover_image,
        description: markReadBook.description || '',
        page_count: markReadBook.page_count || 0,
        genres: markReadBook.genres || [],
        primary_genre: readCustomGenre || readPrimaryGenre,
        grade: readGrade,
        rank_in_group: rankInGroup,
        date_read: format(readDate, 'yyyy-MM-dd'),
        is_first_read: isFirstRead,
        comment: readComment
      }, { withCredentials: true });
      
      // Remove the book from the list after marking as read
      try {
        await axios.delete(`${API}/book-lists/${listId}/books/${markReadBook.book_id}`, { withCredentials: true });
      } catch (removeError) {
        console.error('Failed to remove book from list:', removeError);
        // Don't show error - the book was still marked as read successfully
      }
      
      toast.success(`Marked "${markReadBook.title}" as read and removed from list!`);
      setMarkReadBook(null);
      setReadComparisonStep(false);
      fetchReadBooks();
      fetchList(); // Refresh the list to show the book has been removed
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as read');
    } finally {
      setMarkingRead(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!list?.share_code) {
      // Generate share link first
      try {
        const res = await axios.post(`${API}/book-lists/${listId}/share-link`, {}, { withCredentials: true });
        const shareUrl = `${window.location.origin}/join/${res.data.share_code}?type=book`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied!');
        fetchList();
      } catch (error) {
        toast.error('Failed to generate share link');
      }
    } else {
      const shareUrl = `${window.location.origin}/join/${list.share_code}?type=book`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied!');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const res = await axios.get(`${API}/books/search?query=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setSearchResults(res.data.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddBook = async (book, position = null) => {
    if (position === 'top' || position === 'bottom') {
      // Direct add at position
      try {
        await axios.post(`${API}/book-lists/${listId}/books`, {
          book_id: book.book_id,
          title: book.title,
          author: book.author,
          cover_image: book.cover_image,
          description: book.description,
          page_count: book.page_count,
          genres: book.genres,
          series: book.series,
          position
        }, { withCredentials: true });
        
        toast.success(`Added to ${position === 'top' ? 'top' : 'bottom'} of list`);
        fetchList();
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchResultDetails(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add book');
      }
    } else {
      // Start drag-and-drop ranking flow
      const existingBooks = list?.books || [];
      if (existingBooks.length === 0) {
        // No existing books, add directly
        await handleAddBook(book, 'top');
      } else {
        // Add the book first, then open ranking dialog
        try {
          const res = await axios.post(`${API}/book-lists/${listId}/books`, {
            book_id: book.book_id,
            title: book.title,
            author: book.author,
            cover_image: book.cover_image,
            description: book.description,
            page_count: book.page_count,
            genres: book.genres,
            series: book.series,
            position: 'bottom'
          }, { withCredentials: true });
          
          // Set up drag-and-drop ranking with updated list
          setAddingBook(book);
          setRankingBooks(res.data.books || []);
          setShowRanking(true);
          setSearchOpen(false);
          setSearchQuery('');
          setSearchResults([]);
          setSearchResultDetails(null);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to add book');
        }
      }
    }
  };

  const handleViewSearchResult = async (book) => {
    setLoadingSearchDetails(true);
    try {
      const details = await getBookDetails(book.book_id);
      setSearchResultDetails(details);
    } catch (error) {
      // Use basic book info if API fails
      setSearchResultDetails({
        book_id: book.book_id,
        title: book.title,
        author: book.author,
        cover_image: book.cover_image,
        description: book.description || '',
        page_count: book.page_count || 0,
        genres: book.genres || []
      });
    } finally {
      setLoadingSearchDetails(false);
    }
  };

  const handleSaveRankingOrder = async (orderedBooks) => {
    setSaving(true);
    try {
      const newOrder = orderedBooks.map(b => b.book_id);
      
      await axios.put(`${API}/book-lists/${listId}/reorder`, {
        book_order: newOrder
      }, { withCredentials: true });
      
      toast.success(`Ranked "${addingBook?.title}"!`);
      resetRankingDialog();
      fetchList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const resetRankingDialog = () => {
    setAddingBook(null);
    setShowRanking(false);
    setRankingBooks([]);
  };

  // View book details
  const handleViewDetails = async (book) => {
    setSelectedBook(book);
    setLoadingDetails(true);
    try {
      // Use canonical media API for book details
      const details = await getBookDetails(book.book_id);
      setBookDetails(details);
    } catch (error) {
      console.error('Canonical book details failed, falling back:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/books/${book.book_id}`, { withCredentials: true });
        setBookDetails(response.data);
      } catch (e) {
        // Use basic book info if API fails
        setBookDetails({
          book_id: book.book_id,
          title: book.title,
          author: book.author,
          cover_image: book.cover_image,
          description: book.description || '',
          page_count: book.page_count || 0,
          genres: book.genres || []
        });
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRemoveBook = async (book) => {
    if (!window.confirm(`Remove "${book.title}" from this list?`)) return;
    
    try {
      await axios.delete(`${API}/book-lists/${listId}/books/${book.book_id}`, { withCredentials: true });
      toast.success('Removed from list');
      fetchList();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over || active.id === over.id) return;
    
    // For joint lists, only allow reordering your own ranking
    const books = isJointList ? getMyBooks() : (list?.books || []);
    const oldIndex = books.findIndex(b => b.book_id === active.id);
    const newIndex = books.findIndex(b => b.book_id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const reorderedBooks = arrayMove(books, oldIndex, newIndex);
    
    // Optimistically update UI
    if (isJointList) {
      // Update user's ranking in the list
      setList(prev => {
        const newRankings = [...(prev.user_rankings || [])];
        const myRankingIdx = newRankings.findIndex(ur => ur.user_id === user?.user_id);
        if (myRankingIdx >= 0) {
          newRankings[myRankingIdx] = {
            ...newRankings[myRankingIdx],
            book_order: reorderedBooks.map(b => b.book_id)
          };
        }
        return { ...prev, user_rankings: newRankings };
      });
    } else {
      setList(prev => ({
        ...prev,
        books: reorderedBooks.map((b, i) => ({ ...b, rank: i + 1 }))
      }));
    }
    
    // Save to backend
    try {
      if (isJointList) {
        await axios.put(`${API}/book-lists/${listId}/my-ranking`, {
          book_order: reorderedBooks.map(b => b.book_id)
        }, { withCredentials: true });
      } else {
        await axios.put(`${API}/book-lists/${listId}/reorder`, {
          book_order: reorderedBooks.map(b => b.book_id)
        }, { withCredentials: true });
      }
    } catch (error) {
      toast.error('Failed to save order');
      fetchList(); // Revert on error
    }
  };

  const handleRename = async () => {
    if (!newTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      await axios.put(`${API}/book-lists/${listId}?title=${encodeURIComponent(newTitle)}&description=${encodeURIComponent(newDescription)}`, {}, { withCredentials: true });
      toast.success('List updated');
      setRenameOpen(false);
      fetchList();
    } catch (error) {
      toast.error('Failed to update list');
    }
  };

  const openRenameDialog = () => {
    setNewTitle(list?.title || '');
    setNewDescription(list?.description || '');
    setRenameOpen(true);
  };

  const isBookInList = (bookId) => list?.books?.some(b => b.book_id === bookId);

  const activeBook = activeId ? list?.books?.find(b => b.book_id === activeId) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <AppHeader user={user} showBack={true} backPath="/books" />
      
      <main className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${isJointList ? 'bg-purple-500' : 'bg-amber-500'}`}>
              {isJointList ? <Users className="w-6 h-6 text-white" /> : <BookOpen className="w-6 h-6 text-white" />}
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-[#3c405b]">{list?.title}</h1>
              {list?.description && (
                <p className="text-[#64748B] text-sm">{list.description}</p>
              )}
              <div className="flex items-center gap-2 text-[#94A3B8] text-sm">
                <span>{list?.books?.length || 0} books</span>
                {isJointList && (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {memberCount} {memberCount === 1 ? 'member' : 'members'}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {isJointList && isOwner && (
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleCopyShareLink}
                data-testid="share-link-btn"
              >
                <Link className="w-4 h-4" />
              </Button>
            )}
            {isOwner && (
              <Button variant="outline" size="icon" onClick={openRenameDialog}>
                <Edit2 className="w-4 h-4" />
              </Button>
            )}
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => setSearchOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Book
            </Button>
          </div>
        </div>

        {/* Joint List Empty State for New Joiners */}
        {isJointList && list?.current_user_has_empty_list && (
          <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-purple-50 to-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-[#3c405b] text-lg">Welcome to the list!</h3>
                  <p className="text-[#64748B] text-sm mt-1">
                    Start adding books to build your personal ranking. Your picks will be combined with others to find common favorites.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Ranking View Toggle for Joint Lists */}
        {isJointList && (list?.books?.length > 0 || list?.combined_view) && (
          <div className="mb-4">
            <Tabs value={rankingView} onValueChange={setRankingView}>
              <TabsList className="bg-white/80 p-1">
                <TabsTrigger 
                  value="combined" 
                  className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
                  data-testid="ranking-view-combined"
                >
                  Combined
                </TabsTrigger>
                {getAllMembers().map(member => (
                  <TabsTrigger
                    key={member.user_id}
                    value={member.user_id}
                    className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700 flex items-center gap-1"
                    data-testid={`ranking-view-${member.user_id}`}
                  >
                    {member.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <p className="text-xs text-[#64748B] mt-2">
              {rankingView === 'combined' 
                ? 'Common books ranked by popularity, plus unique picks from each member'
                : `Viewing ${getAllMembers().find(m => m.user_id === rankingView)?.name || 'member'}'s personal ranking`
              }
            </p>
          </div>
        )}
        
        {/* Books List - Different view based on combined vs individual */}
        {!isJointList || rankingView !== 'combined' ? (
          // Personal list OR individual member view
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardContent className="p-4">
              {(isJointList ? getDisplayBooks() : list?.books || []).length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium mb-2">No books yet</p>
                  <p className="text-sm mb-4">Start adding books to this list!</p>
                  <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => setSearchOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Book
                  </Button>
                </div>
              ) : (
                <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  modifiers={[restrictToVerticalAxis]}
                >
                  <SortableContext
                    items={(isJointList ? getDisplayBooks() : list?.books || []).map(b => b.book_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {(isJointList ? getDisplayBooks() : list?.books || []).map((book) => (
                        <SortableBookItem
                          key={book.book_id}
                          book={book}
                          onRemove={handleRemoveBook}
                          onViewDetails={handleViewDetails}
                          onMarkRead={openMarkRead}
                          isRead={isBookRead(book.book_id)}
                          isDragging={activeId === book.book_id}
                          disabled={isJointList && rankingView !== 'combined' && rankingView !== user?.user_id}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                
                {createPortal(
                  <DragOverlay modifiers={[snapCenterToCursor]}>
                    {activeBook && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-lg border-2 border-amber-500">
                        <GripVertical className="w-5 h-5 text-slate-400" />
                        <span className="font-outfit font-bold text-lg text-[#3c405b] w-8 text-center">
                          {activeBook.rank}
                        </span>
                        <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100">
                          {activeBook.cover_image ? (
                            <img src={activeBook.cover_image} alt={activeBook.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[#3c405b] truncate">{activeBook.title}</h3>
                          <p className="text-sm text-[#64748B] truncate">{activeBook.author}</p>
                        </div>
                      </div>
                    )}
                  </DragOverlay>,
                  document.body
                )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          // Joint list combined view
          list?.combined_view ? (
            <CombinedBookView combinedView={list.combined_view} />
          ) : (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardContent className="p-4 text-center py-12 text-slate-500">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">No rankings yet</p>
                <p className="text-sm mb-4">Add books and rank them to see the combined view!</p>
              </CardContent>
            </Card>
          )
        )}
      </main>
      
      {/* Search Dialog */}
      <Dialog open={searchOpen && !showRanking} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">Add Book to List</DialogTitle>
            <DialogDescription>Search for a book to add</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title or author..."
                className="pl-9"
                data-testid="list-book-search-input"
              />
            </div>
            <Button type="submit" disabled={searching} className="bg-amber-500 hover:bg-amber-600">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
          </form>
          
          <div className="flex-1 overflow-auto mt-4 -mx-6 px-6">
            {searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map((book, index) => {
                  const alreadyInList = isBookInList(book.book_id);
                  return (
                    <div key={book.book_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl" data-testid={`search-result-${index}`}>
                      <div 
                        className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
                        onClick={() => handleViewSearchResult(book)}
                      >
                        {book.cover_image ? (
                          <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-slate-300" />
                          </div>
                        )}
                      </div>
                      
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => handleViewSearchResult(book)}
                      >
                        <h3 className="font-medium text-[#3c405b] text-sm truncate hover:text-amber-600 transition-colors">{book.title}</h3>
                        <p className="text-xs text-[#64748B] truncate">{book.author}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSearchResult(book)}
                          className="rounded-full h-8"
                          data-testid={`view-info-btn-${index}`}
                        >
                          <Info className="w-3 h-3 mr-1" />
                          Info
                        </Button>
                        
                        {alreadyInList ? (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Added
                          </span>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-8" data-testid={`add-to-list-btn-${index}`}>
                                <Plus className="w-3 h-3 mr-1" /> Add
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleAddBook(book, 'top')} data-testid={`quick-add-top-${index}`}>
                                <ArrowUp className="w-4 h-4 mr-2" />Add to Top
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddBook(book, 'bottom')} data-testid={`quick-add-bottom-${index}`}>
                                <ArrowDown className="w-4 h-4 mr-2" />Add to Bottom
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleAddBook(book)} data-testid={`add-and-rank-${index}`}>
                                <Trophy className="w-4 h-4 mr-2" />Add & Rank
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : searchQuery && !searching ? (
              <div className="text-center py-8 text-slate-500">
                <p>No books found for &ldquo;{searchQuery}&rdquo;</p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Drag-and-Drop Ranking Dialog */}
      <Dialog open={showRanking} onOpenChange={() => resetRankingDialog()}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="rank-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b] flex items-center gap-2">
              <ListOrdered className="w-5 h-5 text-amber-500" />
              Rank Your Priority
            </DialogTitle>
            <DialogDescription>
              Drag <span className="font-semibold text-amber-500">{addingBook?.title}</span> to set your reading priority.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[400px] overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                
                const oldIndex = rankingBooks.findIndex(b => b.book_id === active.id);
                const newIndex = rankingBooks.findIndex(b => b.book_id === over.id);
                
                if (oldIndex !== -1 && newIndex !== -1) {
                  setRankingBooks(arrayMove(rankingBooks, oldIndex, newIndex));
                }
              }}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={rankingBooks.map(b => b.book_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {rankingBooks.map((book, idx) => (
                    <SortableRankBookItem
                      key={book.book_id}
                      book={book}
                      rank={idx + 1}
                      isNew={book.book_id === addingBook?.book_id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="ghost" 
              onClick={() => {
                resetRankingDialog();
                fetchList();
              }}
              className="rounded-full"
            >
              Skip
            </Button>
            <Button 
              data-testid="save-rank-btn"
              onClick={() => handleSaveRankingOrder(rankingBooks)}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Result Book Details Dialog */}
      <Dialog open={!!searchResultDetails} onOpenChange={() => setSearchResultDetails(null)}>
        <DialogContent className="max-w-lg" data-testid="search-result-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">Book Details</DialogTitle>
          </DialogHeader>
          
          {loadingSearchDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : searchResultDetails && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-24 h-36 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                  {searchResultDetails.cover_image ? (
                    <img 
                      src={searchResultDetails.cover_image} 
                      alt={searchResultDetails.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-bold text-xl text-[#3c405b]">{searchResultDetails.title}</h3>
                  <p className="text-[#64748B] mt-1">{searchResultDetails.author}</p>
                  {searchResultDetails.page_count > 0 && (
                    <p className="text-sm text-slate-600 mt-2">{searchResultDetails.page_count} pages</p>
                  )}
                  {searchResultDetails.published_date && (
                    <p className="text-sm text-slate-600">Published: {searchResultDetails.published_date}</p>
                  )}
                </div>
              </div>
              
              {searchResultDetails.genres?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {searchResultDetails.genres.map((genre) => (
                    <span key={genre} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                      {genre}
                    </span>
                  ))}
                </div>
              )}
              
              {searchResultDetails.description && (
                <div className="max-h-48 overflow-y-auto">
                  <p className="text-sm text-slate-600">{searchResultDetails.description}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                {!isBookInList(searchResultDetails.book_id) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Add to List
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center">
                      <DropdownMenuItem onClick={() => { setSearchResultDetails(null); handleAddBook(searchResultDetails, 'top'); }}>
                        <ArrowUp className="w-4 h-4 mr-2" />Add to Top
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSearchResultDetails(null); handleAddBook(searchResultDetails, 'bottom'); }}>
                        <ArrowDown className="w-4 h-4 mr-2" />Add to Bottom
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setSearchResultDetails(null); handleAddBook(searchResultDetails); }}>
                        <Trophy className="w-4 h-4 mr-2" />Add & Rank
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button variant="outline" onClick={() => setSearchResultDetails(null)} className="rounded-full">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">Edit List</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#3c405b]">List Name</label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter list name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3c405b]">Description (optional)</label>
              <Input
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="What's this list about?"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleRename}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Book Details Dialog */}
      <Dialog open={!!selectedBook} onOpenChange={() => { setSelectedBook(null); setBookDetails(null); }}>
        <DialogContent className="max-w-lg" data-testid="book-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">Book Details</DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : bookDetails && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-24 h-36 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                  {bookDetails.cover_image ? (
                    <img 
                      src={bookDetails.cover_image} 
                      alt={bookDetails.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-bold text-xl text-[#3c405b]">{bookDetails.title}</h3>
                  <p className="text-[#64748B] mt-1">{bookDetails.author}</p>
                  {bookDetails.page_count > 0 && (
                    <p className="text-sm text-slate-600 mt-2">{bookDetails.page_count} pages</p>
                  )}
                  {bookDetails.published_date && (
                    <p className="text-sm text-slate-600">Published: {bookDetails.published_date}</p>
                  )}
                </div>
              </div>
              
              {bookDetails.genres?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {bookDetails.genres.map((genre) => (
                    <span key={genre} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                      {genre}
                    </span>
                  ))}
                </div>
              )}
              
              {bookDetails.description && (
                <div className="max-h-48 overflow-y-auto">
                  <p className="text-sm text-slate-600">{bookDetails.description}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                {bookDetails.preview_link && (
                  <Button variant="outline" asChild className="flex-1">
                    <a href={bookDetails.preview_link} target="_blank" rel="noopener noreferrer">
                      <Info className="w-4 h-4 mr-2" />
                      Preview on Google Books
                    </a>
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setSelectedBook(null); setBookDetails(null); }}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Mark as Read Dialog */}
      <Dialog open={!!markReadBook} onOpenChange={() => { setMarkReadBook(null); setReadComparisonStep(false); }}>
        <DialogContent className="max-w-md" data-testid="mark-read-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">
              {readComparisonStep ? 'Rank Your Book' : 'Mark as Read'}
            </DialogTitle>
            <DialogDescription>
              {readComparisonStep 
                ? 'Which book do you prefer?' 
                : `How did you find "${markReadBook?.title}"?`}
            </DialogDescription>
          </DialogHeader>
          
          {!readComparisonStep ? (
            <div className="space-y-4 py-4">
              {/* Grade Selection */}
              <div className="space-y-2">
                <Label>Your Rating</Label>
                <RadioGroup value={readGrade} onValueChange={setReadGrade} className="flex gap-2">
                  <div className="flex-1">
                    <RadioGroupItem value="liked" id="read-liked" className="peer sr-only" />
                    <Label
                      htmlFor="read-liked"
                      className="flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all peer-data-[state=checked]:border-green-500 peer-data-[state=checked]:bg-green-50"
                    >
                      <ThumbsUp className="w-6 h-6 text-green-500 mb-1" />
                      <span className="text-sm font-medium">Liked</span>
                    </Label>
                  </div>
                  <div className="flex-1">
                    <RadioGroupItem value="alright" id="read-alright" className="peer sr-only" />
                    <Label
                      htmlFor="read-alright"
                      className="flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all peer-data-[state=checked]:border-yellow-500 peer-data-[state=checked]:bg-yellow-50"
                    >
                      <Meh className="w-6 h-6 text-yellow-500 mb-1" />
                      <span className="text-sm font-medium">Alright</span>
                    </Label>
                  </div>
                  <div className="flex-1">
                    <RadioGroupItem value="disliked" id="read-disliked" className="peer sr-only" />
                    <Label
                      htmlFor="read-disliked"
                      className="flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all peer-data-[state=checked]:border-red-500 peer-data-[state=checked]:bg-red-50"
                    >
                      <ThumbsDown className="w-6 h-6 text-red-500 mb-1" />
                      <span className="text-sm font-medium">Disliked</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* First Read */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="first-read"
                  checked={isFirstRead}
                  onChange={(e) => setIsFirstRead(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="first-read" className="text-sm">First time reading this book</Label>
              </div>
              
              {/* Date */}
              <div className="space-y-2">
                <Label>Date Read</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(readDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={readDate}
                      onSelect={(date) => date && setReadDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Primary Genre Selection */}
              {markReadBook?.genres?.length > 1 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-amber-500" />
                    Primary Genre (for stats)
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {markReadBook.genres.map((genre) => {
                      const gConfig = getGenreConfig(genre);
                      const GIcon = gConfig?.icon || BookOpen;
                      const isSelected = readPrimaryGenre === genre && !readCustomGenre;
                      return (
                        <Button
                          key={genre}
                          type="button"
                          variant={isSelected ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => { setReadPrimaryGenre(genre); setReadCustomGenre(''); }}
                          className={isSelected ? 'bg-amber-500 hover:bg-amber-600' : ''}
                        >
                          <GIcon className={`w-3.5 h-3.5 mr-1 ${isSelected ? '' : gConfig?.color?.split(' ')[1] || 'text-slate-600'}`} />
                          {genre}
                        </Button>
                      );
                    })}
                    {/* Custom Genre Option */}
                    <Button
                      type="button"
                      variant={readPrimaryGenre === 'custom' || readCustomGenre ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setReadPrimaryGenre('custom')}
                      className={readPrimaryGenre === 'custom' || readCustomGenre ? 'bg-purple-500 hover:bg-purple-600' : ''}
                      data-testid="genre-select-custom"
                    >
                      + Custom
                    </Button>
                  </div>
                  {/* Custom Genre Input */}
                  {(readPrimaryGenre === 'custom' || readCustomGenre) && (
                    <div className="mt-2">
                      <Input
                        type="text"
                        placeholder="Enter your custom genre..."
                        value={readCustomGenre}
                        onChange={(e) => setReadCustomGenre(e.target.value)}
                        className="text-sm"
                        data-testid="custom-genre-input"
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Comment */}
              <div className="space-y-2">
                <Label>Thoughts (optional)</Label>
                <Textarea
                  value={readComment}
                  onChange={(e) => setReadComment(e.target.value)}
                  placeholder="What did you think about this book?"
                  rows={3}
                />
              </div>
              
              <DialogFooter className="pt-2">
                <Button
                  onClick={handleProceedToRanking}
                  disabled={!readGrade || markingRead}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full w-full"
                  data-testid="continue-to-ranking-btn"
                >
                  {markingRead ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Continue to Ranking
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-4">
              {/* Head-to-head comparison */}
              <p className="text-center text-[#64748B] mb-4">
                Which do you prefer?
              </p>
              <div className="grid grid-cols-2 gap-4">
                {/* New book */}
                <button
                  onClick={() => handleReadComparisonChoice(true)}
                  disabled={markingRead}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-center"
                >
                  <div className="w-20 h-28 mx-auto rounded-lg overflow-hidden mb-3 shadow-md bg-slate-100">
                    {markReadBook?.cover_image ? (
                      <img src={markReadBook.cover_image} alt={markReadBook.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-[#3c405b] text-sm truncate">{markReadBook?.title}</p>
                  <p className="text-xs text-amber-500 mt-1">New</p>
                </button>
                
                {/* Comparison item */}
                <button
                  onClick={() => handleReadComparisonChoice(false)}
                  disabled={markingRead}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-center"
                >
                  <div className="w-20 h-28 mx-auto rounded-lg overflow-hidden mb-3 shadow-md bg-slate-100">
                    {readComparisonItem?.cover_image ? (
                      <img src={readComparisonItem.cover_image} alt={readComparisonItem.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-[#3c405b] text-sm truncate">{readComparisonItem?.title}</p>
                  <p className="text-xs text-slate-500 mt-1">#{readComparisonItem?.rank_in_group}</p>
                </button>
              </div>
              
              {markingRead && (
                <div className="flex items-center justify-center mt-4">
                  <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                  <span className="ml-2 text-[#64748B]">Saving...</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
